import { formatarDia, Periodo } from './Dashboards.periodo'
import type { Parceiro, Parceiros } from './Dashboards.scopes'
import { COR, diasDoPeriodo } from './Operacao.graficos'

const numero = (n: number) => n.toLocaleString('pt-BR')

/**
 * Consultas de madrugada (0h às 6h em São Paulo) em volume: o sinal de uso automatizado. Pessoas quase não consultam
 * nesse horário; um robô que consulta o dia inteiro fica perto de 25% (6 das 24 horas). A partir de 15%, com pelo
 * menos 200 consultas no período, o ranking marca o parceiro.
 */
export const MADRUGADA_SUSPEITA = 0.15
export const CONSULTAS_MINIMAS_SUSPEITA = 200

export function pareceAutomatizado(parceiro: Pick<Parceiro, 'consultas' | 'madrugada'>): boolean {
    return (parceiro.madrugada ?? 0) >= MADRUGADA_SUSPEITA && parceiro.consultas >= CONSULTAS_MINIMAS_SUSPEITA
}

/** Parceiros distintos que consultaram em cada dia. Uma série: o título nomeia. */
export function opcaoAtivosPorDia(porDia: Parceiros['porDia'], periodo: Periodo) {
    const dias = diasDoPeriodo(periodo)
    const porData = new Map(porDia.map((d) => [d.dia, d]))
    return {
        grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (itens: { dataIndex: number }[]) => {
                const dia = dias[itens[0].dataIndex]
                const d = porData.get(dia)
                return `${formatarDia(dia, true)}<br/>${numero(d?.parceiros ?? 0)} parceiros<br/>${numero(
                    d?.consultas ?? 0
                )} consultas`
            }
        },
        xAxis: {
            type: 'category',
            data: dias.map((d) => formatarDia(d)),
            axisLine: { lineStyle: { color: COR.eixo } },
            axisTick: { show: false },
            axisLabel: { color: COR.texto, hideOverlap: true }
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: COR.grade } },
            axisLabel: { color: COR.texto, formatter: (v: number) => numero(v) }
        },
        series: [
            {
                name: 'Parceiros',
                type: 'bar',
                barMaxWidth: 28,
                itemStyle: { color: COR.serie1, borderRadius: [4, 4, 0, 0] },
                data: dias.map((dia) => porData.get(dia)?.parceiros ?? 0)
            }
        ]
    }
}

/** Quantos parceiros em cada faixa de consultas, com o número escrito em cima da barra. */
export function opcaoFaixas(faixas: Parceiros['faixas']) {
    return {
        grid: { left: 8, right: 16, top: 24, bottom: 24, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (itens: { dataIndex: number }[]) => {
                const f = faixas[itens[0].dataIndex]
                return `${f.faixa} ${f.faixa === '1' ? 'consulta' : 'consultas'}<br/>${numero(
                    f.parceiros
                )} parceiros<br/>${numero(f.consultas)} consultas no total`
            }
        },
        xAxis: {
            type: 'category',
            data: faixas.map((f) => f.faixa),
            name: 'consultas no período',
            nameLocation: 'middle',
            nameGap: 28,
            nameTextStyle: { color: COR.texto },
            axisLine: { lineStyle: { color: COR.eixo } },
            axisTick: { show: false },
            axisLabel: { color: COR.texto }
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: COR.grade } },
            axisLabel: { color: COR.texto, formatter: (v: number) => numero(v) }
        },
        series: [
            {
                name: 'Parceiros',
                type: 'bar',
                barMaxWidth: 48,
                itemStyle: { color: COR.serie1, borderRadius: [4, 4, 0, 0] },
                label: {
                    show: true,
                    position: 'top',
                    color: COR.texto,
                    formatter: (p: { value: number }) => numero(p.value)
                },
                data: faixas.map((f) => f.parceiros)
            }
        ]
    }
}
